import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaHeadset,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaTicketAlt,
  FaTags,
  FaPaperPlane,
  FaChevronDown,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import config from "../../config";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bookingId: "",
    category: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const formatPhoneDisplay = (digits = "") => {
    if (!digits) return "";
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    let digits = value.replace(/\D/g, "");

    if (digits.startsWith("91") && digits.length > 10) {
      digits = digits.slice(2);
    }

    digits = digits.slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      phone: digits,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      handlePhoneChange(e);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const form = e.target;
    if (!form.checkValidity()) {
      const firstInvalid = form.querySelector(':invalid');
      
      if (firstInvalid) {
        const yOffset = -150;
        const y = firstInvalid.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({ top: y, behavior: 'smooth' });
        setTimeout(() => {
          firstInvalid.focus();
          form.reportValidity(); 
        }, 400);
      }
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (
      !formData.name ||
      !formData.email ||
      !formData.category ||
      !formData.message
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${config.API_BASE_URL || "{{appUrl}}"}/customer/contactus`,
        formData
      );

      if (response.data.success) {
        toast.success(response.data.message || "Query submitted successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          bookingId: "",
          category: "",
          message: "",
        });
      } else {
        toast.error(response.data.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(
        error.response?.data?.message || "Server error. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex items-center justify-center">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(249, 115, 22, 0.4); }
          50% { box-shadow: 0 0 40px rgba(249, 115, 22, 0.6); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-delay-1 { animation-delay: 0.1s; }
        .animate-delay-2 { animation-delay: 0.2s; }
        .animate-delay-3 { animation-delay: 0.3s; }
        .animate-delay-4 { animation-delay: 0.4s; }
        .animate-delay-5 { animation-delay: 0.5s; }
        .animate-delay-6 { animation-delay: 0.6s; }
        .group:hover .group-hover-rotate {
          transform: rotate(360deg);
        }
        .input-focus-ring {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .input-focus-ring:focus {
          transform: translateY(-2px);
        }
      `}</style>

      <div className="w-full animate-slide-up">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
          <div className="grid lg:grid-cols-5">
            {/* Left Info Panel */}
            <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-700 p-10 lg:p-12 text-white relative overflow-hidden">
              {/* Animated background circles */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
              <div
                className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/20 rounded-full blur-3xl animate-pulse"
                style={{ animationDelay: "1s" }}
              ></div>

              <div className="relative z-10 flex flex-col justify-center items-center text-center h-full">
                <div className="w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mb-6 shadow-2xl animate-float relative">
                  <div className="absolute inset-0 rounded-full bg-white/20 animate-pulse-glow"></div>
                  <FaHeadset className="text-6xl relative z-10 transition-transform duration-700 group-hover:rotate-12" />
                </div>

                <h3 className="text-3xl font-bold mb-4 animate-fade-in animate-delay-1">
                  We're Here to Help!
                </h3>

                <p className="text-blue-100 text-base leading-relaxed mb-8 animate-fade-in animate-delay-2 max-w-sm">
                  Have questions about bookings, payments, or need technical
                  support? Our team is ready to assist you.
                </p>

                <div className="space-y-4 w-full max-w-sm animate-fade-in animate-delay-3">
                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                    <div className="w-10 h-10 bg-orange-500/30 rounded-full flex items-center justify-center">
                      <FaClock className="text-lg" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Quick Response</p>
                      <p className="text-xs text-blue-200">Within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                    <div className="w-10 h-10 bg-blue-500/30 rounded-full flex items-center justify-center">
                      <FaCheckCircle className="text-lg" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Expert Support</p>
                      <p className="text-xs text-blue-200">Dedicated team</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form Panel */}
            <div className="lg:col-span-3 p-8 lg:p-12">
              <div className="mb-8 animate-fade-in">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  Get in Touch
                </h2>
                <p className="text-gray-600">
                  Fill out the form below and we'll get back to you soon
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                {/* Name */}
                <div className="animate-fade-in animate-delay-1">
                  <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                    <FaUser className="text-orange-500" /> Full Name{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("name")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your full name"
                    className={`w-full px-5 py-4 border-2 rounded-2xl focus:outline-none transition-all duration-300 input-focus-ring ${
                      focusedField === "name"
                        ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                        : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                    }`}
                    required
                  />
                </div>

                {/* Email */}
                <div className="animate-fade-in animate-delay-2">
                  <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                    <FaEnvelope className="text-orange-500" /> Email{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="you@example.com"
                    className={`w-full px-5 py-4 border-2 rounded-2xl focus:outline-none transition-all duration-300 input-focus-ring ${
                      focusedField === "email"
                        ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                        : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                    }`}
                    required
                  />
                </div>

                {/* Phone & Booking ID */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="animate-fade-in animate-delay-3">
                    <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                      <FaPhoneAlt className="text-orange-500" /> Phone{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-5 flex items-center text-gray-500 font-medium pointer-events-none z-10">
                        +91
                      </span>
                      <input
                        type="text"
                        name="phone"
                        value={formatPhoneDisplay(formData.phone)}
                        onChange={handlePhoneChange}
                        onFocus={() => setFocusedField("phone")}
                        onBlur={() => setFocusedField(null)}
                        placeholder="98765 54321"
                        className={`w-full pl-20 pr-5 py-4 border-2 rounded-2xl focus:outline-none transition-all duration-300 input-focus-ring ${
                          focusedField === "phone"
                            ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                            : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                        }`}
                        maxLength="15"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 ml-1">
                      10-digit mobile number
                    </p>
                  </div>

                  <div className="animate-fade-in animate-delay-4">
                    <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                      <FaTicketAlt className="text-orange-500" /> Booking ID{" "}
                      <span className="text-gray-400 text-xs font-normal">
                        (Optional)
                      </span>
                    </label>
                    <input
                      type="text"
                      name="bookingId"
                      value={formData.bookingId}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("bookingId")}
                      onBlur={() => setFocusedField(null)}
                      placeholder="e.g., BK-123456"
                      className={`w-full px-5 py-4 border-2 rounded-2xl focus:outline-none transition-all duration-300 input-focus-ring ${
                        focusedField === "bookingId"
                          ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                          : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                      }`}
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="animate-fade-in animate-delay-5">
                  <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                    <FaTags className="text-orange-500" /> Category{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("category")}
                      onBlur={() => setFocusedField(null)}
                      className={`w-full px-5 py-4 pr-12 border-2 rounded-2xl focus:outline-none transition-all duration-300 appearance-none cursor-pointer input-focus-ring ${
                        focusedField === "category"
                          ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                          : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                      }`}
                      required
                    >
                      <option value="">Choose a category</option>
                      <option value="Booking Issue">Booking Issue</option>
                      <option value="Payment Issue">Payment Issue</option>
                      <option value="Account / Profile">
                        Account / Profile
                      </option>
                      <option value="Technical Support">
                        Technical Support
                      </option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-5 pointer-events-none">
                      <FaChevronDown className="text-gray-400 text-sm transition-transform duration-300 group-hover:translate-y-1" />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="animate-fade-in animate-delay-6">
                  <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 text-sm">
                    <FaPaperPlane className="text-orange-500" /> Message{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Describe your query in detail..."
                    rows="5"
                    className={`w-full px-5 py-4 border-2 rounded-2xl focus:outline-none transition-all duration-300 resize-none input-focus-ring ${
                      focusedField === "message"
                        ? "border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50/30"
                        : "border-gray-200 hover:border-gray-300 bg-gray-50/50"
                    }`}
                    required
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 hover:from-orange-600 hover:via-orange-700 hover:to-orange-600 text-white font-bold py-5 rounded-2xl shadow-xl transition-all duration-500 transform hover:scale-[1.02] hover:shadow-2xl disabled:opacity-70 disabled:cursor-not-allowed disabled:scale-100 disabled:hover:shadow-xl flex items-center justify-center gap-3 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                  {loading ? (
                    <span className="flex items-center gap-3 relative z-10">
                      <svg
                        className="animate-spin h-6 w-6 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-3 relative z-10">
                      <FaPaperPlane className="group-hover:translate-x-2 group-hover:-translate-y-1 transition-transform duration-300 group-hover-rotate" />
                      <span className="text-lg">Submit Query</span>
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
